import { observer } from "mobx-react-lite";
import React, { useEffect, useState } from "react";
import { Card, Container } from "react-bootstrap";
import { toast } from "react-toastify";
import OrderItem from "../../api/models/addOnTesting/ShoppingCart";
import Address from "../../api/models/Address";
import { APIError } from "../../api/ObservableFromFetch";
import { addToCart, getAvailableTests, getBillingAddress, getCart, getOrdered, getProcessing, removeFromCart } from "../../api/ShoppingCart";
import { useTranslationWithRef } from "../../hooks/useTranslationWithRef";
import { useAuthStore } from "../../stores/StoreProvider";
import AddressForm from "../Results/Modals/AddressForm";
import { CartStep } from "./CartStep";
import { ConsentStep } from "./ConsentStep";
import { Header } from "./Header";
import { PanelDetail } from "./PanelDetail";
import { PaymentProblem } from "./PaymentProblem";
import PaymentStep from "./PaymentStep";
import { PaymentSuccess } from "./PaymentSuccess";
import { ShoppingCartStep } from "./ShoppingCartStep";
import { TestsAvailable } from "./TestsAvailable";

interface CartProps {
  testEventId: number
  requireBillingAddress?: boolean
}

const Cart = (props: CartProps) => {
  const [translationRef, t] = useTranslationWithRef()
  const [invalidate, setInvalidate] = useState<number>(0)
  const [processing, setProcessing] = useState(false)
  const [processingOrders, setProcessingOrders] = useState(false)
  const [billingAddress, setBillingAddress] = useState<Address>()
  const authStore = useAuthStore()

  useEffect(() => {
    const subscription = getAvailableTests(props.testEventId).subscribe({
      next: (results: OrderItem[]) => setItems(results),
      error: (e: APIError) => toast.error(translationRef.current(e.message, { data: e.data }))
    })

    return () => subscription.unsubscribe()
  }, [props.testEventId, invalidate, translationRef])

  useEffect(() => {
    const subscription = getCart(props.testEventId).subscribe({
      next: (results: OrderItem[]) => {
        setCartItems(results)
        setProcessing(false)
      },
      error: (e: APIError) => toast.error(translationRef.current(e.message, { data: e.data }))
    })

    return () => subscription.unsubscribe()
  }, [props.testEventId, invalidate, translationRef])

  useEffect(() => {
    const subscription = getOrdered(props.testEventId).subscribe({
      next: (results: OrderItem[]) => setPreviousOrders(results),
      error: (e: APIError) => toast.error(translationRef.current(e.message, { data: e.data }))
    })

    return () => subscription.unsubscribe()
  }, [props.testEventId, translationRef])

  useEffect(() => {
    const subscription = getProcessing(props.testEventId).subscribe({
      next: (result: boolean) => setProcessingOrders(result),
      error: (e: APIError) => toast.error(translationRef.current(e.message, { data: e.data }))
    })

    return () => subscription.unsubscribe()
  }, [props.testEventId, invalidate, translationRef])


  useEffect(() => {
    const subscription = getBillingAddress(props.testEventId).subscribe({
      next: (result: Address) => setBillingAddress(result),
      error: (e: APIError) => toast.error(translationRef.current(e.message, { data: e.data }))
    })

    return () => subscription.unsubscribe()
  }, [props.testEventId, invalidate, translationRef])


  const [items, setItems] = useState<OrderItem[]>([])          // available
  const [cartItems, setCartItems] = useState<OrderItem[]>([])  // in cart
  const [previousOrders, setPreviousOrders] = useState<OrderItem[]>([])  // previous orders
  const [displayedPanel, setDisplayedPanel] = useState<OrderItem | null>(null)

  const [prevStep, setPrevStep] = useState<CartStep>(CartStep.Unknown)
  const [currentStep, setCurrentStep] = useState<CartStep>(CartStep.TestsAvailable)

  const showPanelDetailsPage = (item: OrderItem) => {
    setDisplayedPanel(item)
    setPrevStep(CartStep.TestsAvailable)
    setCurrentStep(CartStep.PanelDetails)
  }

  const goToShoppingCart = () => {
    setPrevStep(CartStep.TestsAvailable)
    setCurrentStep(CartStep.ShoppingCart)
  }

  const goToTestsAvailable = () => {
    setPrevStep(CartStep.Unknown)
    setCurrentStep(CartStep.TestsAvailable)
  }

  const goToConsent = () => {
    setPrevStep(CartStep.ShoppingCart)
    setCurrentStep(CartStep.Consent)
  }

  const goToPayment = () => {
    setPrevStep(CartStep.ShoppingCart)
    setCurrentStep(props.requireBillingAddress ? CartStep.PaymentAddress : CartStep.Payment)
  }

  const showCartDetailsPage = (item: OrderItem) => {
    setDisplayedPanel(item)
    setPrevStep(CartStep.ShoppingCart)
    setCurrentStep(CartStep.PanelDetails)
  }

  const addCart = (values: number[]) => {
    setProcessing(true)
    setItems(items => items.filter(i => !values.includes(i.addOnTestingPanelId ?? 0)))

    addToCart({ id: props.testEventId, ids: values }).subscribe({
      next: () => {
        setInvalidate(prev => prev + 1)
      },
      error: (e: APIError) => {
        setInvalidate(prev => prev + 1)
        toast.error(t(e.message, { data: e.data }))
      }
    })
  }

  const removeCart = (item: OrderItem) => {
    setProcessing(true)
    setCartItems(items => items.filter(i => i.addOnTestingPanelId !== item.addOnTestingPanelId))

    removeFromCart(item.addOnTestingOrderItemId!, props.testEventId).subscribe({
      next: () => {
        setInvalidate(prev => prev + 1)
        if (cartItems.length === 1) {
          goToTestsAvailable()
        }
      },
      error: (e: APIError) => {
        setInvalidate(prev => prev + 1)
        toast.error(t(e.message, { data: e.data }))
      }
    })
  }

  const invalidateCart = () => {
    setInvalidate(prev => prev + 1)
  }

  return (
    <Container className="mt-5">
      <Header count={cartItems.length} step={currentStep} />

      <Card className="shadow-lg rounded-lg border-0">
        <Card.Body className="p-4">
          {currentStep === CartStep.PanelDetails && displayedPanel && <PanelDetail panel={displayedPanel}
            onAdd={prevStep !== CartStep.ShoppingCart ? () => {
              addCart([displayedPanel.addOnTestingPanelId ?? 0])
              setDisplayedPanel(null)
              setCurrentStep(prevStep)
              setPrevStep(CartStep.Unknown)
            } : undefined}
            onRemove={prevStep === CartStep.ShoppingCart ? () => {
              removeCart(displayedPanel)
              setDisplayedPanel(null)
              setCurrentStep(prevStep)
              setPrevStep(CartStep.TestsAvailable)
            } : undefined}
            onClose={() => {
              setDisplayedPanel(null)
              setCurrentStep(prevStep)
              setPrevStep(CartStep.Unknown)
            }} />}
          {currentStep === CartStep.TestsAvailable &&
            <TestsAvailable items={items} cartItems={cartItems} previousOrders={previousOrders} processing={processing}
              processingOrders={processingOrders}
              onExpand={showPanelDetailsPage} onAddCart={addCart} onNext={goToShoppingCart} />}

          {currentStep === CartStep.ShoppingCart &&
            <ShoppingCartStep items={cartItems} onExpand={showCartDetailsPage} processing={processing}
              onRemove={removeCart} onBack={goToTestsAvailable} onNext={goToConsent} />
          }

          {currentStep === CartStep.Consent && <div hidden={currentStep !== CartStep.Consent}>
            <ConsentStep cartItems={cartItems} testEventId={props.testEventId} invalidate={invalidateCart}
              onBack={() => {
                setPrevStep(CartStep.TestsAvailable)
                setCurrentStep(CartStep.ShoppingCart)
              }}
              onSubmit={goToPayment} />
          </div>}

          {currentStep === CartStep.PaymentAddress && <AddressForm includeName includeEmail testEventId={props.testEventId}
            address={{ address1: "", address2: "", city: "", state: "AL", zip: "", email: "", ...authStore.donorInfo, ...billingAddress }} onCancel={() => {
              setPrevStep(CartStep.TestsAvailable)
              setCurrentStep(CartStep.ShoppingCart)
            }} onSubmit={(address: Address) => {
              setBillingAddress(address)
              setPrevStep(CartStep.PaymentAddress)
              setCurrentStep(CartStep.Payment)
            }} />}

          {currentStep === CartStep.Payment && <PaymentStep items={cartItems} address={billingAddress} testEventId={props.testEventId} onBack={() => {
            setPrevStep(CartStep.ShoppingCart)
            setCurrentStep(props.requireBillingAddress ? CartStep.PaymentAddress : CartStep.ShoppingCart)
          }} onNext={(success: boolean) => {
            setPrevStep(CartStep.Payment)
            setCurrentStep(success ? CartStep.PaymentSuccess : CartStep.PaymentProblem)
          }} />}

          {currentStep === CartStep.PaymentProblem && <PaymentProblem onNext={() => { setInvalidate(prev => prev + 1); goToShoppingCart() }} />}

          {currentStep === CartStep.PaymentSuccess && <PaymentSuccess onNext={() => { setInvalidate(prev => prev + 1); goToTestsAvailable() }} />}

        </Card.Body>
      </Card>
    </Container>
  )
}

export default observer(Cart)
